import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Clock, Phone, Mail, Check, Star } from "lucide-react";

const Pricing = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="text-center"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              Pricing & Services
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Transparent, compassionate pricing for our home visit services. No
              hidden fees, just clear costs for the care your pet deserves.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Main Pricing Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Home Euthanasia Pricing */}
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="card"
            >
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Clock className="w-10 h-10 text-primary-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Home Euthanasia
                </h2>
                <p className="text-gray-600 mb-6">
                  Compassionate end-of-life care in the comfort of your own home
                </p>
              </div>

              <div className="space-y-6">
                <div className="bg-primary-50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-lg font-semibold text-primary-800">
                      Standard Hours
                    </span>
                    <span className="text-2xl font-bold text-primary-600">
                      £280
                    </span>
                  </div>
                  <p className="text-primary-700 text-sm">8am to 9pm</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-lg font-semibold text-gray-800">
                      Emergency Hours
                    </span>
                    <span className="text-2xl font-bold text-gray-600">
                      £380
                    </span>
                  </div>
                  <p className="text-gray-700 text-sm">9pm to 8am</p>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900">
                    What's Included:
                  </h3>
                  <ul className="space-y-2">
                    {[
                      "Home visit consultation",
                      "Professional veterinary care",
                      "Sedation if required",
                      "Euthanasia procedure",
                      "Compassionate support",
                      "Aftercare guidance",
                    ].map((item, index) => (
                      <li key={index} className="flex items-center space-x-3">
                        <Check className="w-5 h-5 text-secondary-500 flex-shrink-0" />
                        <span className="text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* Quality of Life Assessment */}
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="card"
            >
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Star className="w-10 h-10 text-secondary-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Quality of Life Assessment
                </h2>
                <p className="text-gray-600 mb-6">
                  Professional evaluation to help you make informed decisions
                </p>
              </div>

              <div className="text-center mb-8">
                <div className="text-4xl font-bold text-secondary-600 mb-2">
                  £135
                </div>
                <p className="text-gray-600">One-time home visit assessment</p>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">
                  Assessment Includes:
                </h3>
                <ul className="space-y-2">
                  {[
                    "Home visit consultation",
                    "Clinical examination",
                    "Medical history review",
                    "Quality of life evaluation",
                    "Treatment alternatives discussion",
                    "Written assessment report",
                  ].map((item, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <Check className="w-5 h-5 text-secondary-500 flex-shrink-0" />
                      <span className="text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-8 p-4 bg-secondary-50 rounded-lg">
                <p className="text-secondary-800 text-sm text-center">
                  <strong>Note:</strong> This assessment can help determine if
                  euthanasia is the right choice, or if alternative treatments
                  might improve your pet's quality of life.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Additional Information */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What to Expect
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Understanding the process helps prepare you and your family for
              this difficult time.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Before the Visit",
                items: [
                  "Initial consultation call",
                  "Scheduling at your convenience",
                  "Preparation guidance",
                  "What to expect explanation",
                ],
              },
              {
                title: "During the Visit",
                items: [
                  "Professional veterinary care",
                  "Compassionate support",
                  "Family involvement options",
                  "Dignified procedure",
                ],
              },
              {
                title: "After the Visit",
                items: [
                  "Aftercare options discussion",
                  "Cremation arrangements",
                  "Grief support resources",
                  "Follow-up if needed",
                ],
              },
            ].map((phase, index) => (
              <motion.div
                key={phase.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {phase.title}
                </h3>
                <ul className="space-y-2">
                  {phase.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-primary-400 rounded-full flex-shrink-0"></div>
                      <span className="text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment & Booking */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Payment & Booking
            </h2>
            <p className="text-xl text-gray-600">
              We accept various payment methods and can discuss payment options
              during your consultation.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="card"
            >
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Payment Methods
              </h3>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <Check className="w-5 h-5 text-secondary-500" />
                  <span className="text-gray-600">Cash</span>
                </li>
                <li className="flex items-center space-x-3">
                  <Check className="w-5 h-5 text-secondary-500" />
                  <span className="text-gray-600">Bank Transfer</span>
                </li>
                <li className="flex items-center space-x-3">
                  <Check className="w-5 h-5 text-secondary-500" />
                  <span className="text-gray-600">Card Payment</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="card"
            >
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Booking Process
              </h3>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center text-sm font-bold text-primary-600">
                    1
                  </div>
                  <span className="text-gray-600">
                    Initial consultation call
                  </span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center text-sm font-bold text-primary-600">
                    2
                  </div>
                  <span className="text-gray-600">Schedule appointment</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center text-sm font-bold text-primary-600">
                    3
                  </div>
                  <span className="text-gray-600">Home visit service</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Need Support or Guidance?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              I'm here to provide compassionate support and guidance during this
              difficult time. Every consultation is confidential and
              understanding.
            </p>
            <div className="flex justify-center">
              <a
                href="/contact#contact-form"
                className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-medium py-3 px-8 rounded-lg transition-all duration-200 text-center"
              >
                Contact Me
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
